import type { InsightsRow } from "../api/gigAdmin";

/*
 * Insights: the admin's own master-list CSV, joined to engine records and analysed in this browser only. The file is
 * never uploaded or stored; it lives in memory until the page is closed. Pure functions, so they're unit-tested.
 */

export interface MasterList {
  name: string;
  head: string[];
  rows: string[][];
}

/** Quoted CSV with "" escapes; the delimiter (comma, semicolon or tab) is whichever splits the header most. */
export function parseCsv(input: string): { head: string[]; rows: string[][] } {
  const text = input.replace(/^\uFEFF/, "");
  const first = text.split(/\r?\n/, 1)[0] ?? "";
  const delimiter = [",", ";", "\t"].sort(
    (a, b) => first.split(b).length - first.split(a).length,
  )[0];
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  const endRow = () => {
    row.push(cell);
    cell = "";
    if (row.some((x) => x.trim())) rows.push(row);
    row = [];
  };
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += c;
    } else if (c === '"') quoted = true;
    else if (c === delimiter) {
      row.push(cell);
      cell = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      endRow();
    } else cell += c;
  }
  endRow();
  return { head: (rows.shift() ?? []).map((h) => h.trim()), rows };
}

/** First header matching `pattern`; failing that, the first column whose values pass `test` (in the first 50 rows). */
export function guessColumn(
  head: string[],
  rows: string[][],
  pattern: RegExp,
  test?: (value: string) => boolean,
): number {
  let index = head.findIndex((h) => pattern.test(h));
  if (index < 0 && test) {
    const sample = rows.slice(0, 50);
    index = head.findIndex(
      (_, c) =>
        sample.filter((r) => test(r[c] ?? "")).length > 25 ||
        (sample.length > 0 && sample.every((r) => test(r[c] ?? ""))),
    );
  }
  return index;
}

export const guessEmailColumn = (head: string[], rows: string[][]) =>
  guessColumn(head, rows, /e-?mail/i, (v) => /@/.test(v));
export const guessIdColumn = (head: string[], rows: string[][]) =>
  guessColumn(head, rows, /participant|^p\.?id$|gtw|^id$/i);

export const ageBand = (age: number) =>
  age < 18
    ? "Under 18"
    : age <= 24
      ? "18 to 24"
      : age <= 29
        ? "25 to 29"
        : age <= 35
          ? "30 to 35"
          : "36 and over";

/** A cell's group for slicing: age and date-of-birth columns are banded; blanks are "(blank)". */
export function segmentValue(
  head: string[],
  row: string[],
  column: number,
  now = Date.now(),
): string {
  const header = head[column] ?? "";
  const value = String(row[column] ?? "").trim();
  if (!value) return "(blank)";
  if (/\b(dob|birth)/i.test(header)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime()))
      return ageBand(Math.floor((now - date.getTime()) / 31557600000));
  }
  if (/\bage\b/i.test(header) && !Number.isNaN(Number(value))) return ageBand(Number(value));
  return value.replace(/\s+/g, " ").replace(/^./, (m) => m.toUpperCase());
}

/*
 * Columns that can slice the funnel: 2 to 40 distinct values, not nearly unique per person, and not names, emails,
 * phones, IDs or addresses. Gender, then state, come first when present.
 */
export function segmentColumns(
  master: Pick<MasterList, "head" | "rows">,
  emailCol: number,
  idCol: number,
): number[] {
  const n = master.rows.length;
  const rank = (c: number) =>
    /gender|sex/i.test(master.head[c] ?? "")
      ? 0
      : /state|region|location|lga/i.test(master.head[c] ?? "")
        ? 1
        : 2;
  return master.head
    .map((_, c) => c)
    .filter((c) => {
      if (c === emailCol || c === idCol) return false;
      if (/name|e-?mail|phone|^id$|address/i.test(master.head[c] ?? "")) return false;
      const distinct = new Set(master.rows.map((r) => segmentValue(master.head, r, c))).size;
      return distinct >= 2 && distinct <= 40 && (n < 10 || distinct < n * 0.8);
    })
    .sort((a, b) => rank(a) - rank(b));
}

export interface JoinedRecord {
  row: string[];
  /** The engine participant this master record matched, by email then participant ID. */
  engine: InsightsRow | null;
}

export function joinMaster(
  master: Pick<MasterList, "rows">,
  emailCol: number,
  idCol: number,
  engine: InsightsRow[],
): JoinedRecord[] {
  const byEmail = new Map(engine.map((r) => [r.email.toLowerCase(), r]));
  const byId = new Map(
    engine.filter((r) => r.participantId).map((r) => [String(r.participantId).toLowerCase(), r]),
  );
  return master.rows.map((row) => {
    const email =
      emailCol >= 0
        ? String(row[emailCol] ?? "")
            .trim()
            .toLowerCase()
        : "";
    let match = email ? (byEmail.get(email) ?? null) : null;
    if (!match && idCol >= 0) {
      const pid = String(row[idCol] ?? "")
        .trim()
        .toLowerCase();
      if (pid) match = byId.get(pid) ?? null;
    }
    return { row, engine: match };
  });
}

/** Engine participants nobody on the master list matched (different email, or missing from the list). */
export function notInMaster(engine: InsightsRow[], joined: JoinedRecord[]): InsightsRow[] {
  const seen = new Set(joined.map((j) => j.engine?.email).filter(Boolean));
  return engine.filter((r) => !seen.has(r.email));
}

export const GIG_STEP_NAMES = ["Position", "Package", "Pitch", "Verify", "Certificate"];

export interface Stage {
  label: string;
  short: string;
  test: (j: JoinedRecord) => boolean;
}

export const STAGES: Stage[] = [
  { label: "Started", short: "Started", test: (j) => Boolean(j.engine) },
  ...GIG_STEP_NAMES.map((name, i) => ({
    label: `Step ${i + 1} ${name}`,
    short: `Step ${i + 1}`,
    test: (j: JoinedRecord) => Boolean(j.engine?.stepAt[i]),
  })),
  { label: "Youth-in-Work", short: "Youth-in-Work", test: (j) => Boolean(j.engine?.youthInWork) },
];

export const pct = (n: number, d: number) => (d ? Math.round((n / d) * 100) : 0);

export interface FunnelStage {
  label: string;
  count: number;
  pct: number;
}

export function funnel(joined: JoinedRecord[]): FunnelStage[] {
  return STAGES.map((s) => {
    const count = joined.filter(s.test).length;
    return { label: s.label, count, pct: pct(count, joined.length) };
  });
}

/** Where a group loses the most people: the largest fall between consecutive stage rates, if any. */
export function biggestDrop(
  rates: number[],
  labels: string[] = STAGES.map((s) => s.label),
): { at: string; points: number } | null {
  let drop: { at: string; points: number } | null = null;
  for (let i = 1; i < rates.length; i++) {
    const points = (rates[i - 1] ?? 0) - (rates[i] ?? 0);
    if (!drop || points > drop.points) drop = { at: labels[i] ?? "", points };
  }
  return drop && drop.points > 0 ? drop : null;
}

const average = (values: number[]) =>
  values.length ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : null;

export interface SliceGroup {
  value: string;
  people: number;
  /** Share of the group (%) at each of STAGES. */
  rates: number[];
  counts: number[];
  drop: { at: string; points: number } | null;
  avgBid: number | null;
  /** Share referred for finance (%), or null when the viewer can't see finance. */
  financeReferred: number | null;
}

export function sliceBy(
  head: string[],
  joined: JoinedRecord[],
  column: number,
  showFinance: boolean,
  now = Date.now(),
): SliceGroup[] {
  const groups = new Map<string, JoinedRecord[]>();
  for (const j of joined) {
    const key = segmentValue(head, j.row, column, now);
    groups.set(key, [...(groups.get(key) ?? []), j]);
  }
  return [...groups.entries()]
    .sort((a, b) => b[1].length - a[1].length)
    .map(([value, g]) => {
      const counts = STAGES.map((s) => g.filter(s.test).length);
      const rates = counts.map((c) => pct(c, g.length));
      return {
        value,
        people: g.length,
        rates,
        counts,
        drop: biggestDrop(rates),
        avgBid: average(g.map((j) => j.engine?.bidReadiness).filter((x): x is number => x != null)),
        financeReferred: showFinance
          ? pct(g.filter((j) => j.engine?.financeReferred).length, g.length)
          : null,
      };
    });
}

export function median(values: number[]): number | null {
  if (!values.length) return null;
  const s = [...values].sort((x, y) => x - y);
  const m = s.length >> 1;
  return s.length % 2 ? (s[m] as number) : ((s[m - 1] as number) + (s[m] as number)) / 2;
}

export interface StepGap {
  from: number;
  to: number;
  /** Median days, or null when nobody has both dates. */
  median: number | null;
  people: number;
}

/** Typical days between consecutive steps, for everyone on the master list who reached the later step. */
export function stepGaps(joined: JoinedRecord[]): StepGap[] {
  return GIG_STEP_NAMES.slice(1).map((_, i) => {
    const days = joined
      .map((j) => {
        const a = j.engine?.stepAt[i];
        const b = j.engine?.stepAt[i + 1];
        return a && b ? (new Date(b).getTime() - new Date(a).getTime()) / 864e5 : null;
      })
      .filter((x): x is number => x != null && Number.isFinite(x) && x >= 0);
    return { from: i + 1, to: i + 2, median: median(days), people: days.length };
  });
}

export function describeDays(days: number | null): string {
  if (days == null) return "–";
  if (days < 1) return "under 1 day";
  const n = Math.round(days);
  return `${n} day${n === 1 ? "" : "s"}`;
}

/** Every cell quoted, quotes doubled: same as v2's csvDownload. */
export function toCsv(head: string[], rows: unknown[][]): string {
  const cell = (x: unknown) => `"${String(x == null ? "" : x).replace(/"/g, '""')}"`;
  return [head.map(cell).join(","), ...rows.map((r) => r.map(cell).join(","))].join("\n");
}

/** Builds the CSV in the browser and saves it; nothing goes to the server. */
export function downloadCsv(name: string, head: string[], rows: unknown[][]): void {
  const blob = new Blob([toCsv(head, rows)], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
